import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";
import Frame from "../shell/Frame";
import Meta from "../shell/Meta";
import Metrics from "../parts/Metrics";
import Caps from "../parts/Caps";
import Approach from "../parts/Approach";
import WorkGrid from "../parts/WorkGrid";
import Sectors from "../parts/Sectors";
import Invite from "../parts/Invite";

const Home = () => (
  <Frame>
    <Meta />

    {/* HERO */}
    <section className="hero">
      <div className="shell">
        <div className="layout">
          <div>
            <span className="eyebrow">Technology &amp; Internet Services</span>
            <h1>
              Engineering the technology stack <span className="strike"><span className="em">your business</span></span> actually depends on.
            </h1>
            <p className="lede" style={{ marginTop: "1.5rem" }}>
              BTCRuka is an engineering-led firm. Software, networks, cloud, security and managed operations — delivered
              as one accountable practice rather than a relay race of vendors.
            </p>
            <div className="signature">
              <Link to="/services" className="btn btn--mint">
                See capabilities <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn--line">
                Talk to an engineer
              </Link>
            </div>
          </div>

          {/* Terminal-mock visual */}
          <div className="term">
            <div className="term-head">
              <span className="left">
                <span className="dots"><span /><span /><span /></span>
                <span>btcruka@studio · ~/engagement</span>
              </span>
              <span className="badge">ONLINE</span>
            </div>
            <div className="term-body">
              <span className="ln"><span className="gray">$</span> <span className="cmd">btcruka</span> <span className="arg">--audit</span></span>
              <span className="ln"><span className="gray">  ✓ inventory: 124 systems, 38 vendors</span></span>
              <span className="ln"><span className="gray">  ✓ gaps: 11 identified, 3 critical</span></span>
              <span className="ln"><span className="gray">$</span> <span className="cmd">btcruka</span> <span className="arg">--architect</span></span>
              <span className="ln"><span className="gray">  ✓ target: hybrid-cloud, segmented network</span></span>
              <span className="ln"><span className="gray">  ✓ phased over 14 months · risk register signed</span></span>
              <span className="ln"><span className="gray">$</span> <span className="cmd">btcruka</span> <span className="arg">--ship</span></span>
              <span className="ln"><span className="ok">  ↳ release ready · monitored · SLAs bound</span></span>
              <span className="ln"><span className="gray">$</span> <span className="blink"></span></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Metrics />

    {/* CAPABILITIES */}
    <section className="band">
      <div className="shell">
        <div className="section-head">
          <div className="left">
            <span className="eyebrow">Capabilities</span>
            <h2>Eleven disciplines, one accountable team.</h2>
            <p>From a single CRM rollout to a full IT department on retainer, every engagement runs on the same disciplined delivery model.</p>
          </div>
          <div className="right">
            <Link to="/services" className="btn btn--ghost">All capabilities <FiArrowUpRight /></Link>
          </div>
        </div>
        <Caps limit={6} />
      </div>
    </section>

    {/* APPROACH */}
    <section className="band band--lines">
      <div className="shell">
        <div className="section-head">
          <div className="left">
            <span className="eyebrow">How we deliver</span>
            <h2>Engineered to be boring on purpose.</h2>
            <p>Surprises in technology programmes are almost never good. Our four-stage delivery makes progress visible early — and problems visible earlier.</p>
          </div>
        </div>
        <Approach />
      </div>
    </section>

    {/* SELECTED WORK */}
    <section className="band">
      <div className="shell">
        <div className="section-head">
          <div className="left">
            <span className="eyebrow">Selected work</span>
            <h2>Shipped recently, under NDA.</h2>
            <p>A representative sample of recent engagements. Clients and identifying detail are anonymised; the technical shape is real.</p>
          </div>
          <div className="right">
            <Link to="/work" className="btn btn--ghost">Full work archive <FiArrowUpRight /></Link>
          </div>
        </div>
        <WorkGrid limit={6} />
      </div>
    </section>

    {/* WHY US (duet) */}
    <section className="band band--lines">
      <div className="shell">
        <div className="duet">
          <div>
            <span className="eyebrow">Why BTCRuka</span>
            <h2>One engineering team — not a relay of vendors.</h2>
            <p>
              Mid-market and enterprise customers are exhausted from stitching together eight different suppliers to keep their
              technology working. BTCRuka brings software, networking, cloud, security and managed operations into a single
              practice, governed by one accountable team.
            </p>
            <ul className="tick">
              <li><span className="m" /> Senior-led delivery against contracted, measurable SLAs</li>
              <li><span className="m" /> Vendor-independent advice across cloud, security and software</li>
              <li><span className="m" /> Fully documented handover — no black-box lock-in</li>
              <li><span className="m" /> Regional presence with full English-language delivery</li>
            </ul>
            <div style={{ marginTop: "2rem" }}>
              <Link to="/about" className="btn btn--line">About the studio <FiArrowUpRight /></Link>
            </div>
          </div>

          <div className="terminal-block">
            <div className="head">
              <span>commitments.txt</span>
              <span className="badge">SIGNED</span>
            </div>
            <div className="grid2">
              <div className="cell">
                <div className="lbl">SLA-01 / RESPONSE</div>
                <div className="v">≤ 4h</div>
                <div className="n">Tier-2 incident first response</div>
              </div>
              <div className="cell">
                <div className="lbl">SLA-02 / RESOLUTION</div>
                <div className="v">≤ 24h</div>
                <div className="n">P1 target time to resolution</div>
              </div>
              <div className="cell">
                <div className="lbl">SLA-03 / UPTIME</div>
                <div className="v">99.95%</div>
                <div className="n">Managed estate, trailing 90 d</div>
              </div>
              <div className="cell">
                <div className="lbl">SLA-04 / CSAT</div>
                <div className="v">≥ 4.6</div>
                <div className="n">Out of 5, rolling 12 mo</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* SECTORS */}
    <section className="band">
      <div className="shell">
        <div className="section-head">
          <div className="left">
            <span className="eyebrow">Sectors</span>
            <h2>Built around the businesses we already understand.</h2>
            <p>Our engineers have shipped and operated systems inside each of these sectors. We bring that context to the first meeting.</p>
          </div>
        </div>
        <Sectors />
      </div>
    </section>

    <Invite />
  </Frame>
);

export default Home;
